/**
 * The ONE in-flight primitive (R3). While an async surface is loading, it renders
 * these token-tinted placeholders instead of a zero-count / blank surface, so the
 * layout doesn't jump when the real data lands and an agent (or a person) can tell
 * "still loading" from "nothing here yet". The shimmer + reduced-motion fallback live
 * in the `.skeleton*` rules in index.html; the async container carries `aria-busy`.
 *
 * A skeleton is NEVER an empty-state — "noch nichts hier" copy stays `.surface-empty`.
 */

import { html, type TemplateResult } from "@platform/web-kit/render"

/** N placeholder nav-cards matching the `.compass-card` footprint (hub loading). */
export function skeletonCards(n: number): TemplateResult {
  return html`${Array.from(
    { length: n },
    () => html`
      <div class="skeleton-card" aria-hidden="true">
        <span class="skeleton skeleton-heading"></span>
        <span class="skeleton skeleton-line is-lg"></span>
        <span class="skeleton skeleton-line is-md"></span>
        <span class="skeleton skeleton-chip"></span>
      </div>
    `,
  )}`
}

/** N placeholder list-cards matching the opportunity / application card footprint. */
export function skeletonListCards(n: number): TemplateResult {
  return html`${Array.from(
    { length: n },
    () => html`
      <li class="skeleton-card is-x-list" aria-hidden="true">
        <span class="skeleton skeleton-heading"></span>
        <span class="skeleton skeleton-line is-lg"></span>
        <span class="skeleton skeleton-line is-sm"></span>
      </li>
    `,
  )}`
}

/** N placeholder run-rail / run-ledger rows (a single shimmering bar each). */
export function skeletonRows(n: number): TemplateResult {
  return html`${Array.from(
    { length: n },
    () => html`<div class="skeleton skeleton-row" aria-hidden="true"></div>`,
  )}`
}

/** The balance-figure placeholder — one large shimmering block. */
export function skeletonBalance(): TemplateResult {
  return html`<span class="skeleton skeleton-balance" aria-hidden="true"></span>`
}

/** A block of `n` shimmering text lines (last one short) for prose regions. */
export function skeletonText(n: number): TemplateResult {
  return html`${Array.from(
    { length: n },
    (_, i) => html`<span class="skeleton skeleton-line ${i === n - 1 ? "is-md" : "is-lg"}" aria-hidden="true"></span>`,
  )}`
}
